import { NgClass } from '@angular/common'
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  Injector,
  OnInit,
  Signal,
  computed,
  effect,
  inject,
  input,
  runInInjectionContext,
} from '@angular/core'

import { FlxStyleModule, FlxThemeModule, FlxWrapperStyleDirective } from '@flowx/angular-theme'
import { SkeletonTemplateConfig } from '@flowx/core-sdk'

import { getBoolExpressionSignal } from '../helpers'

@Component({
  selector: 'flx-skeleton-wrapper',
  hostDirectives: [
    { directive: FlxWrapperStyleDirective, inputs: ['flxWrapperStyle: templateConfig'] },
  ],
  imports: [NgClass, FlxThemeModule, FlxStyleModule],
  templateUrl: './skeleton-wrapper.component.html',
  styleUrl: './skeleton-wrapper.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlxSkeletonWrapperComponent implements OnInit, AfterViewInit {
  readonly #injector = inject(Injector)
  readonly #flxWrapperStyle = inject(FlxWrapperStyleDirective)

  templateConfig = input.required<SkeletonTemplateConfig>()

  displayOptions = computed(() => this.templateConfig().displayOptions)
  flowxProps = computed(() => this.displayOptions().flowxProps)

  // expressions
  isHidden: Signal<boolean>

  readonly variant = computed(() => this.flowxProps()?.variant || 'text')

  readonly lines = computed(() => {
    const lines = this.flowxProps()?.lines
    return lines > 0 ? lines : 3
  })

  readonly lineIndexes = computed(() => Array.from({ length: this.lines() }, (_, i) => i))

  readonly variantClass = computed(() => `skeleton--${this.variant()}`)
  readonly animatedClass = computed(() => (this.flowxProps()?.animated ? 'skeleton--animated' : ''))

  readonly hostClass = computed(() =>
    [this.displayOptions().className, this.variantClass(), this.animatedClass()]
      .filter(Boolean)
      .join(' ')
  )

  readonly barClass = (index: number): string =>
    index === this.lines() - 1 ? 'skeleton__bar skeleton__bar--short' : 'skeleton__bar'

  ngOnInit(): void {
    runInInjectionContext(this.#injector, () => {
      const props = this.flowxProps()
      this.isHidden = getBoolExpressionSignal(props.hide, this.templateConfig())
    })
  }

  ngAfterViewInit(): void {
    runInInjectionContext(this.#injector, () => {
      effect(() => {
        this.#flxWrapperStyle.toggleDisplay(this.isHidden())
      })
    })
  }
}
